import { Component, Input, OnInit } from "@angular/core";
import { PageEvent } from "@angular/material/paginator";
import { Logger } from "@app/@core/services/logger.service";
import { AdvicesService } from "@app/@shared/api/advices.service";
import { WorksheetsService } from "@app/@shared/api/worksheets.service";
import { RequestParams, ResultPaginated } from "@app/@shared/models/_results";
import { NotificationService } from "@app/@shared/services/notification.service";
import { finalize } from "rxjs/operators";
import { expandableRowAnimation } from "@app/@shared/animations/expandable-row.animation";

@Component({
  selector: "app-audit",
  templateUrl: "./audit.component.html",
  styleUrls: ["./audit.component.scss"],
  animations: [expandableRowAnimation],
})
export class AuditComponent implements OnInit {
  log = new Logger(AuditComponent.name);
  auditHistory: ResultPaginated<any>;
  displayedColumns: string[] = ["action_op", "action_user", "properties", "#"];
  requestAuditParams = new RequestParams();
  expandedElement: any | null = null;
  isLoading = false;

  private _id: number;
  private _entity: string;

  @Input() set id(value: number) {
    this._id = value;
  }
  get id(): number {
    return this._id;
  }

  @Input() set entity(value: string) {
    this._entity = value;
  }
  get entity(): string {
    return this._entity;
  }

  constructor(
    private advicesService: AdvicesService,
    private worksheetsService: WorksheetsService,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.requestAuditParams.order = [{ type: "desc", field: "created_on" }];
    this.requestAuditParams.filter = [{ value: this.id.toString(), field: "entity_id" }];
    this.getAudit();
  }

  getAudit(): void {
    this.isLoading = true;
    if (this.entity === "advices") {
      this.advicesService
        .getAdvicesAuditHistory(this.requestAuditParams)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: (result) => (this.auditHistory = result),
          error: (err) => {
            this.log.error("Error get audit advices: ", err);
            this.notificationService.open("app.messages.operation_failed");
          },
        });
    } else if (this.entity === "worksheets") {
      this.worksheetsService
        .getWorksheetsAuditHistory(this.requestAuditParams)
        .pipe(finalize(() => (this.isLoading = false)))
        .subscribe({
          next: (result) => (this.auditHistory = result),
          error: (err) => {
            this.log.error("Error get audit worksheets: ", err);
            this.notificationService.open("app.messages.operation_failed");
          },
        });
    }
  }

  /** Returns the diff between this element and the next one in the audit list. */
  getDiff(current: any): any[] {
    const ignoreFields = [
      "id",
      "entity_id",
      "created_on",
      "updated_on",
      "action_op",
      "action_user",
    ];

    const index = this.auditHistory.items.indexOf(current);
    const nextObject =
      index < this.auditHistory.items.length - 1
        ? this.auditHistory.items[index + 1]
        : null;

    const diffs: any[] = [];
    if (!nextObject) return diffs;

    for (const key of Object.keys(current)) {
      if (!ignoreFields.includes(key) && current[key] !== nextObject[key]) {
        diffs.push({
          label: `audit.fields.${key}`,
          before: nextObject[key],
          after: current[key],
          type: this.detectType(current[key], key),
          enumPrefix: this.getEnumPrefix(key),
        });
      }
    }
    return diffs;
  }

  private readonly enumFields: Record<string, string> = {
    status: 'worksheets.worksheet.status.',
    urgency: 'worksheets.worksheet.urgency.',
    return_status: 'worksheets.worksheet.status.',
  };

  /** Detects field type for pretty formatting. */
  private detectType(value: any, key?: string): "date" | "boolean" | "enum" | "text" {
    if (typeof value === "boolean") return "boolean";
    if (typeof value === "string" && /\d{4}-\d{2}-\d{2}T/.test(value)) return "date";
    if (key && this.enumFields[key]) return "enum";
    return "text";
  }

  getEnumPrefix(key: string): string {
    return this.enumFields[key] ?? '';
  }

  /** Converts string date to Date object. */
  asDate(value: any): Date {
    return value ? new Date(value) : null;
  }

  onChangePage(eventPage: PageEvent): void {
    this.requestAuditParams.pagination = {
      pageIndex: eventPage.pageIndex,
      pageSize: eventPage.pageSize,
    };
    this.getAudit();
  }
}
